/**
 * §7.4 單片追溯。與 mockup 的差異：加 PV 號掃碼查詢。
 *
 * 這是整套系統的門面。§9 規則 1 的 `WeighEvent.panelId` 就是為了讓這一頁成立——
 * 「這個欄位是整套系統的存在理由」。
 */

import { useQuery } from '../api/hooks.js'
import { ResolutionBadge, WeightSourceBadge } from '../components/badges.js'
import { PanelLookup } from '../components/PanelLookup.js'
import { Alert, Card, Empty, KV, Mono, Pill, Table, Td } from '../components/ui.js'
import { useDict } from '../config/ConfigContext.js'
import { kg, kgUnit, pct, ts, tsShort } from '../../../shared/lib/format.js'
import { Link, navigate } from '../router.js'

interface Trace {
  panel: {
    panel_id: string
    model_code: string | null
    spec_unknown: boolean
    regulatory_id: string | null
    condition_grade: string
    moisture: string
    source_customer: string
    received_at: string
    status: string
  }
  recovery: {
    intakeKg: number | null
    intakeSource: 'receiving' | 'op1_measured' | null
    shippedKg: number
    ratePct: number | null
    inProgress: boolean
    note: string | null
  }
  balances: {
    materialCode: string
    outputKg: number
    consumedKg: number
    shippedKg: number
    lossKg: number
    balanceKg: number
  }[]
  ledger: {
    ledgerId: string
    materialCode: string
    entryType: string
    qtyKg: number
    stationId: string | null
    workOrderId: string | null
    calcMethod: string
    resolutionGrade: 'R1' | 'R2' | 'R3' | 'R4'
    dataTier: string
    postedAt: string
    note?: string
  }[]
  workOrders: { work_order_id: string; station_id: string; status: string; opened_at: string }[]
  items: { item_id: string; material_code: string; status: string; issued: boolean; share_ratio: number | null; basis_kg: string | null; basis_source: string | null }[]
  weighEvents: { event_id: string; measurement_point_id: string; net_kg: string; entered_by: string; entered_at: string; verified_by: string | null; validation_flags: string[] }[]
}

const ENTRY_LABEL: Record<string, string> = {
  output: '產出', consumed: '消耗', shipped: '出廠',
  disposal: '處置', scrap: '報廢', loss: '損耗', reversal: '沖正',
}

export function PanelDetail({ panelId }: { panelId: string }) {
  const dict = useDict()
  const { data, error, loading } = useQuery<Trace>(
    panelId ? `/api/panels/${encodeURIComponent(panelId)}` : null,
  )

  if (!panelId) {
    return (
      <Card title="單片追溯" subtitle="掃描或輸入 PV 號">
        <PanelLookup onSubmit={(v) => navigate(`/panel/${encodeURIComponent(v)}`)} />
      </Card>
    )
  }

  if (loading) return <p className="text-sm text-ink-500">載入中…</p>
  if (error) {
    return (
      <div className="space-y-3">
        <Alert tone="danger" title="查不到這片">{error.message}</Alert>
        <Card title="換一個 PV 號">
          <PanelLookup onSubmit={(v) => navigate(`/panel/${encodeURIComponent(v)}`)} />
        </Card>
      </div>
    )
  }
  if (!data) return null

  const p = data.panel
  const r = data.recovery

  return (
    <div className="space-y-4">
      <Card
        title={<Mono>{p.panel_id}</Mono>}
        subtitle={`${p.model_code ?? '未辨識型號'} · ${p.source_customer}`}
        right={<Pill tone={p.status === 'closed' ? 'emerald' : p.status === 'scrapped' ? 'red' : 'sky'}>
          {p.status === 'received' ? '已進庫' : p.status === 'wip' ? '在製'
            : p.status === 'closed' ? '已處理完' : p.status === 'scrapped' ? '已報廢' : '已抽離'}
        </Pill>}
      >
        <KV items={[
          ['列管編碼', p.regulatory_id ? <Mono key="r">{p.regulatory_id}</Mono> : '（無）'],
          ['外觀 / 含水', `${p.condition_grade} / ${p.moisture}`],
          ['進廠時間', ts(p.received_at)],
          ['型號', p.spec_unknown ? '未辨識（基準取全型號平均）' : (p.model_code ?? '—')],
        ]} />
      </Card>

      <Card title="回收率" tone={r.note ? 'warn' : 'plain'}>
        <KV items={[
          ['進料重（分母）', r.intakeKg === null ? '（無）' : kgUnit(r.intakeKg, 2)],
          ['分母來源', r.intakeSource === 'op1_measured' ? 'OP-1 逐片實測'
            : r.intakeSource === 'receiving' ? '進庫過磅' : '—'],
          ['已出廠', kgUnit(r.shippedKg, 2)],
          ['回收率', r.ratePct === null ? '—' : (
            <span className="tabular-nums font-semibold">
              {pct(r.ratePct)}{r.inProgress && <span className="ml-2 text-xs font-normal text-ink-500">（在製中，尚未完成）</span>}
            </span>
          )],
        ]} />
        {/* §9 規則 13：進庫未過磅時分母改用 OP-1 逐片合計，並在報表註明 */}
        {r.note && <div className="mt-3"><Alert tone="warn" title="分母說明">{r.note}</Alert></div>}
      </Card>

      <Card title="單片物料餘額" subtitle="同一片、同一物料的收支加總">
        {data.balances.length === 0 ? <Empty>還沒有任何收支分錄。</Empty> : (
          <Table head={['物料', '產出', '消耗', '出廠', '損耗', '餘額']}>
            {data.balances.map((b) => (
              <tr key={b.materialCode}>
                <Td>{dict.material(b.materialCode)}</Td>
                <Td className="tabular-nums">{kg(b.outputKg, 3)}</Td>
                <Td className="tabular-nums">{kg(b.consumedKg, 3)}</Td>
                <Td className="tabular-nums">{kg(b.shippedKg, 3)}</Td>
                <Td className="tabular-nums">{kg(b.lossKg, 3)}</Td>
                <Td className="tabular-nums font-semibold">
                  {Math.abs(b.balanceKg) < 0.001
                    ? <span className="text-ink-400">0（已拆完）</span>
                    : kg(b.balanceKg, 3)}
                </Td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      <Card title="收支帳明細" subtitle={`${data.ledger.length} 筆`}>
        <Table head={['時間', '站台', '物料', '類型', '數量', '解析度', '方法', '備註']} dense>
          {data.ledger.map((l) => (
            <tr key={l.ledgerId} className={l.entryType === 'loss' ? 'bg-amber-50/40' : ''}>
              <Td className="text-xs whitespace-nowrap">{tsShort(l.postedAt)}</Td>
              <Td className="text-xs">
                {l.workOrderId
                  ? <Link to={`/balance/${l.workOrderId}`} className="text-sky-700">{dict.station(l.stationId)}</Link>
                  : dict.station(l.stationId)}
              </Td>
              <Td className="text-xs">{dict.materialShort(l.materialCode)}</Td>
              <Td className="text-xs">{ENTRY_LABEL[l.entryType] ?? l.entryType}</Td>
              <Td className={`tabular-nums text-xs ${l.qtyKg < 0 ? 'text-ink-600' : 'text-emerald-800'}`}>
                {kg(l.qtyKg, 4)}
              </Td>
              <Td><ResolutionBadge grade={l.resolutionGrade} reason="" /></Td>
              <Td className="text-xs text-ink-500">{l.calcMethod}</Td>
              <Td className="text-xs text-ink-500 max-w-[240px]">{l.note ?? ''}</Td>
            </tr>
          ))}
        </Table>
      </Card>

      {/* §9 規則 1：這一片自己的逐片量測 */}
      {data.weighEvents.length > 0 && (
        <Card title="這一片的逐片量測" subtitle="§9 規則 1：panelId 有值的量測事件">
          <Table head={['量測點', '淨重', '輸入人', '時間', '覆核', '旗標']} dense>
            {data.weighEvents.map((w) => (
              <tr key={w.event_id}>
                <Td className="text-xs"><Mono>{w.measurement_point_id}</Mono></Td>
                <Td className="tabular-nums">{kgUnit(Number(w.net_kg), 2)}</Td>
                <Td className="text-xs">{w.entered_by}</Td>
                <Td className="text-xs">{tsShort(w.entered_at)}</Td>
                <Td className="text-xs">{w.verified_by ?? <span className="text-ink-400">—</span>}</Td>
                <Td>{w.validation_flags.map((f) => <Pill key={f} tone="amber">{f}</Pill>)}</Td>
              </tr>
            ))}
          </Table>
        </Card>
      )}

      <Card title="經過的工單">
        {data.workOrders.length === 0 ? <Empty>還沒進過任何工單。</Empty> : (
          <Table head={['工單', '站台', '狀態', '開立時間']} dense>
            {data.workOrders.map((w) => (
              <tr key={w.work_order_id}>
                <Td><Link to={`/balance/${w.work_order_id}`} className="text-sky-700"><Mono>{w.work_order_id}</Mono></Link></Td>
                <Td>{dict.station(w.station_id)}</Td>
                <Td className="text-xs">{w.status === 'open' ? '進行中' : '已關閉'}</Td>
                <Td className="text-xs">{tsShort(w.opened_at)}</Td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      <Card title="目前所屬料件">
        {data.items.length === 0 ? <Empty>—</Empty> : (
          <Table head={['料件', '物料', '狀態', '本片的分攤比例', '分攤基準']} dense>
            {data.items.map((it) => (
              <tr key={`${it.item_id}-${it.issued}`} className={it.issued ? 'text-ink-400' : ''}>
                <Td><Mono>{it.item_id}</Mono></Td>
                <Td className="text-xs">{dict.material(it.material_code)}</Td>
                <Td className="text-xs">{it.issued ? '已領出' : it.status}</Td>
                <Td className="tabular-nums text-xs">
                  {it.share_ratio === null ? '—' : pct(it.share_ratio, 4)}
                </Td>
                <Td className="text-xs">
                  {it.basis_kg === null ? '—' : (
                    <>
                      {kgUnit(Number(it.basis_kg), 3)}
                      <WeightSourceBadge src={it.basis_source === 'measured' ? 'measured' : 'booked'} />
                    </>
                  )}
                </Td>
              </tr>
            ))}
          </Table>
        )}
      </Card>
    </div>
  )
}
